/**
 * Collection filters hook - manages filter state with Jotai
 */
import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';

import type { CollectionFilters } from '@/types/schemas';

const defaultFilters: CollectionFilters = {
  search: undefined,
  colors: undefined,
  colorMode: 'any',
  specialColors: undefined,
  rarities: undefined,
  sets: undefined,
  types: undefined,
  superTypes: undefined,
  cardTypes: undefined,
  cardCategories: undefined,
  cmcValues: undefined,
  formats: undefined,
  finishes: undefined,
  conditions: undefined,
  folderId: undefined,
  priceMin: undefined,
  priceMax: undefined,
  starredOnly: undefined,
  gameChangersOnly: undefined,
  availableOnly: undefined,
  inListFilter: undefined,
  listIdFilter: undefined,
  inDeckFilter: undefined,
  deckIdFilter: undefined,
  roles: undefined,
  keywords: undefined,
  colorIdentities: undefined,
};

const filtersAtom = atom<CollectionFilters>(defaultFilters);

export interface UseCollectionFiltersReturn {
  filters: CollectionFilters;
  setSearch: (search: string) => void;
  setFilter: <K extends keyof CollectionFilters>(key: K, value: CollectionFilters[K]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

export default function useCollectionFilters(): UseCollectionFiltersReturn {
  const [filters, setFilters] = useAtom(filtersAtom);

  const setSearch = useCallback(
    (search: string) => {
      setFilters((prev) => ({ ...prev, search: search || undefined }));
    },
    [setFilters],
  );

  const setFilter = useCallback(
    <K extends keyof CollectionFilters>(key: K, value: CollectionFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    // Preserve folderId when clearing filters (folder is navigation, not a filter)
    setFilters((prev) => ({ ...defaultFilters, folderId: prev.folderId }));
  }, [setFilters]);

  // Check for active filters (excludes folderId which is navigation, not a filter)
  const hasActiveFilters =
    !!filters.search ||
    (filters.colors?.length ?? 0) > 0 ||
    (filters.specialColors?.length ?? 0) > 0 ||
    (filters.rarities?.length ?? 0) > 0 ||
    (filters.sets?.length ?? 0) > 0 ||
    (filters.superTypes?.length ?? 0) > 0 ||
    (filters.cardTypes?.length ?? 0) > 0 ||
    (filters.cardCategories?.length ?? 0) > 0 ||
    (filters.cmcValues?.length ?? 0) > 0 ||
    (filters.conditions?.length ?? 0) > 0 ||
    (filters.finishes?.length ?? 0) > 0 ||
    !!filters.starredOnly ||
    !!filters.gameChangersOnly ||
    !!filters.availableOnly ||
    !!filters.inListFilter ||
    !!filters.inDeckFilter ||
    (filters.roles?.length ?? 0) > 0 ||
    (filters.keywords?.length ?? 0) > 0 ||
    (filters.colorIdentities?.length ?? 0) > 0;

  return {
    filters,
    setSearch,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
